import React from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { AuthPageLayout } from '../components/AuthPageLayout';
import { supabase } from '../lib/supabaseClient';

function toGerman(message: string): string {
  const m = message.toLowerCase();
  if (m.includes('password should be at least') || m.includes('password must be at least'))
    return 'Das Passwort muss mindestens 8 Zeichen lang sein.';
  if (m.includes('weak password')) return 'Das Passwort ist zu schwach. Bitte wähle ein stärkeres.';
  if (m.includes('auth session missing') || m.includes('no session'))
    return 'Sitzung abgelaufen. Bitte fordere einen neuen Reset-Link an.';
  if (m.includes('same password'))
    return 'Das neue Passwort darf nicht identisch mit dem alten sein.';
  if (m.includes('network') || m.includes('fetch'))
    return 'Netzwerkfehler. Bitte prüfe deine Internetverbindung.';
  return message;
}

export function ResetPasswordPage() {
  const navigate = useNavigate();
  const [password, setPassword] = React.useState('');
  const [passwordConfirm, setPasswordConfirm] = React.useState('');
  const [passwordVisible, setPasswordVisible] = React.useState(false);
  const [passwordConfirmVisible, setPasswordConfirmVisible] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [done, setDone] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [sessionReady, setSessionReady] = React.useState(false);

  React.useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY' || event === 'SIGNED_IN') {
        setSessionReady(true);
      }
    });
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) setSessionReady(true);
    });
    return () => subscription.unsubscribe();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password.length < 8) {
      setError('Das Passwort muss mindestens 8 Zeichen lang sein.');
      return;
    }
    if (password !== passwordConfirm) {
      setError('Die Passwörter stimmen nicht überein.');
      return;
    }

    setLoading(true);
    try {
      const { error: updateError } = await supabase.auth.updateUser({ password });
      if (updateError) throw new Error(updateError.message);
      setDone(true);
      window.setTimeout(() => navigate('/login', { replace: true }), 2500);
    } catch (err) {
      const raw = err instanceof Error ? err.message : String(err);
      setError(toGerman(raw));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthPageLayout>
      {done ? (
        <div className="flex flex-col items-center text-center gap-4">
          <div className="w-14 h-14 rounded-full bg-green-50 border border-green-100 flex items-center justify-center">
            <svg className="w-7 h-7 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-slate-900">Passwort gespeichert!</h1>
          <p className="text-sm text-slate-500 leading-relaxed">
            Dein neues Passwort wurde gesetzt. Du wirst gleich zum Login weitergeleitet…
          </p>
        </div>
      ) : (
        <>
          <h1 className="text-2xl font-bold text-slate-900">Neues Passwort setzen</h1>
          <p className="text-sm text-slate-500 mt-1 leading-relaxed">
            Wähle ein neues Passwort für deinen Account.
          </p>

          {!sessionReady ? (
            <p className="mt-6 text-sm text-slate-400">Link wird geprüft…</p>
          ) : (
            <form onSubmit={(e) => { void handleSubmit(e); }} className="mt-6 space-y-4">
              <label className="block">
                <span className="block text-xs font-medium text-slate-600 mb-1.5">Neues Passwort</span>
                <div className="relative">
                  <input
                    type={passwordVisible ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={8}
                    autoFocus
                    autoComplete="new-password"
                    className="w-full border border-app-border rounded-xl px-3.5 py-2.5 pr-10 text-sm outline-none focus:ring-2 focus:ring-[#3b82f6]/25 focus:border-[#3b82f6] transition-all"
                    placeholder="Mindestens 8 Zeichen"
                  />
                  <button
                    type="button"
                    onClick={() => setPasswordVisible((v) => !v)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-slate-400 hover:text-slate-600 hover:bg-slate-100"
                    aria-label={passwordVisible ? 'Passwort verbergen' : 'Passwort anzeigen'}
                    title={passwordVisible ? 'Passwort verbergen' : 'Passwort anzeigen'}
                  >
                    {passwordVisible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </label>

              <label className="block">
                <span className="block text-xs font-medium text-slate-600 mb-1.5">Passwort bestätigen</span>
                <div className="relative">
                  <input
                    type={passwordConfirmVisible ? 'text' : 'password'}
                    value={passwordConfirm}
                    onChange={(e) => setPasswordConfirm(e.target.value)}
                    required
                    minLength={8}
                    autoComplete="new-password"
                    className={`w-full border rounded-xl px-3.5 py-2.5 pr-10 text-sm outline-none focus:ring-2 transition-all ${
                      passwordConfirm && password !== passwordConfirm
                        ? 'border-red-300 focus:ring-red-200 focus:border-red-400'
                        : 'border-app-border focus:ring-[#3b82f6]/25 focus:border-[#3b82f6]'
                    }`}
                    placeholder="Passwort wiederholen"
                  />
                  <button
                    type="button"
                    onClick={() => setPasswordConfirmVisible((v) => !v)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-slate-400 hover:text-slate-600 hover:bg-slate-100"
                    aria-label={passwordConfirmVisible ? 'Passwort verbergen' : 'Passwort anzeigen'}
                    title={passwordConfirmVisible ? 'Passwort verbergen' : 'Passwort anzeigen'}
                  >
                    {passwordConfirmVisible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {passwordConfirm && password !== passwordConfirm && (
                  <span className="mt-1 block text-xs text-red-500">Passwörter stimmen nicht überein.</span>
                )}
              </label>

              {error ? (
                <div className="rounded-xl bg-red-50 border border-red-200 px-3.5 py-3">
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              ) : null}

              <button
                type="submit"
                disabled={loading || password.length < 8 || password !== passwordConfirm}
                className="w-full rounded-xl bg-[#3b82f6] px-4 py-3 text-sm font-semibold text-white hover:bg-blue-600 disabled:opacity-60 transition-colors shadow-sm mt-2"
              >
                {loading ? 'Passwort wird gespeichert…' : 'Passwort speichern'}
              </button>
            </form>
          )}

          <p className="mt-5 text-center text-sm text-slate-500">
            <button
              type="button"
              onClick={() => navigate('/login', { replace: true })}
              className="font-medium text-[#3b82f6] hover:underline"
            >
              ← Zurück zum Login
            </button>
          </p>
        </>
      )}
    </AuthPageLayout>
  );
}
